<script setup lang="ts">
import { object, string } from 'yup'
import type { InferType } from 'yup'
import type { FormSubmitEvent } from '@nuxt/ui'
const { t } = useI18n()

const toast = useToast()
const config = useRuntimeConfig()
const schema = object({
    email: string().email(t("footer.newsletter.emailInvalid")).required(t("footer.newsletter.emailRequired")),
})

type Schema = InferType<typeof schema>

const state = reactive({
    email: undefined,
})

async function onNewsletterSubscription(event: FormSubmitEvent<Schema>) {
    try {
        const email = event.data.email?.trim()
        if (!email) {
            toast.add({
                title: t("footer.newsletter.emailInvalid"),
                color: 'error',
            })
            return
        }

        const body: Record<string, any> = { email }
        const groupId = config.public.mailerLiteNewsGroupID
        if (groupId) body.groups = [groupId]

        const res = await $fetch('https://connect.mailerlite.com/api/subscribers', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${config.public.mailerLiteAPI}`,
                'Content-Type': 'application/json',
            },
            body,
        })

        // MailerLite typically returns { data: { status: 'active', ... } }
        const status = (res as any)?.data?.status
        if (status === 'active') {
            toast.add({
                title: t("footer.newsletter.successful"),
                color: 'success',
            })
            state.email = undefined
        } else {
            toast.add({
                title: t("footer.newsletter.error"),
                color: 'error',
            })
        }
    } catch (error: any) {
        const apiMsg =
            error?.data?.error?.message ||
            error?.data?.message ||
            t("footer.newsletter.error")
        toast.add({
            title: apiMsg,
            color: 'error',
        })
    }
}
</script>

<template>
    <div class="footer-newsletter-shape relative bg-[#FFBD52] flex flex-col justify-center items-center w-[calc(100%+5px)] overflow-hidden lg:w-full max-w-[1000px] lg:mx-auto py-6 lg:py-12 px-10 lg:px-32 lg:-mb-50 -ml-[5px]">
        <div class="footer-newsletter-shape bg-[#FFE3B8] flex justify-center items-center absolute top-0 right-0 bottom-0 left-0 -z-[1] ml-[5px] mb-[5px]"></div>
        <div class="flex flex-col gap-[8px] mb-6">
            <h2 class="text-center uppercase text-[20px] leading-[26px] lg:text-[28px] lg:leading-[38px] text-info">{{ t("footer.newsletter.title") }}</h2>
            <p class="text-center text-info">{{ t("footer.newsletter.description") }}</p>
        </div>
        <UForm :schema="schema" :state="state" @submit="onNewsletterSubscription" class="flex flex-col lg:flex-row gap-4.5 w-full items-center justify-center mb-4">
            <UFormField label="Email" class="w-full max-w-[500px]">
                <UInput v-model="state.email" :placeholder="t('footer.newsletter.placeholder')" class="newsletter-email-input w-full" />
            </UFormField>
            <UButton type="submit" color="info" variant="outline" class="cursor-pointer">{{ t("footer.newsletter.button") }}</UButton>
        </UForm>
        <p class="text-center text-[12px] lg:text-[14px] text-info">{{ t("footer.newsletter.notice") }}</p>
    </div>
    <svg width="0" height="0" style="position:absolute">
        <defs>
            <clipPath id="footer-newsletter-shape" clipPathUnits="objectBoundingBox">
                <path d="
        M 0.940494 1
        L 0.060177 1
        C 0.02436 1 -0.00333 0.898673 0.001181 0.784112
        L 0.025456 0.167593
        C 0.029227 0.071803 0.054503 0 0.084452 0
        L 0.886164 0
        C 0.912748 0 0.936102 0.056887 0.943367 0.139337
        L 0.997694 0.755855
        C 1.00849 0.87828 0.979963 1 0.940494 1
        Z" />
            </clipPath>
        </defs>
    </svg>
    <svg width="0" height="0" style="position:absolute">
        <defs>
            <clipPath id="footer-newsletter-shape-mobile" clipPathUnits="objectBoundingBox">
                <path d="
        M0.8633 0
        C0.9093 0 0.9492 0.0385 0.9593 0.0927
        L1 0.3112
        V1
        H0
        V0.3637
        L0.0616 0.0879
        C0.0732 0.036 0.1121 0 0.1566 0
        H0.8633
        Z" />
            </clipPath>
        </defs>
    </svg>
</template>

<style>
.footer-newsletter-shape {
    clip-path: url(#footer-newsletter-shape);
    @media (max-width: 1023px) {
        clip-path: url(#footer-newsletter-shape-mobile);
    }
}
.newsletter-email-input input {
    color: #401D03 !important;
    @media (max-width: 1023px) {
        text-align: center;
    }
}
</style>
